import { useQuery } from "@blitzjs/rpc"
import { Sub } from "@superwall-me/schema"
import { Localizer } from "app/localization/liquid"
import getLocalizedStrings from "app/localization/queries/getLocalizedStrings"
import { useMemo, useRef } from "react"

type TProps = {
  applicationId: number
  rawSubs?: Sub[]
  paywallPreviewLanguage?: {
    label: string
    value: string
  }
}

const useLocalizedSubstitutions = ({
  applicationId,
  rawSubs = [],
  paywallPreviewLanguage = { label: "en", value: "en" },
}: TProps) => {
  const [localizedStrings, { isLoading }] = useQuery(
    getLocalizedStrings,
    { applicationId, locale: paywallPreviewLanguage.value },
    { suspense: false, enabled: paywallPreviewLanguage.value !== "" }
  )

  const localizer = useMemo(() => {
    return new Localizer(localizedStrings ?? {})
  }, [localizedStrings])

  const previousLanguage = useRef(paywallPreviewLanguage.value)
  const previousRawSubs = useRef(rawSubs)
  const previousLocalizedSubs = useRef<Sub[]>([])

  if (previousLanguage.current !== paywallPreviewLanguage.value) {
    previousLanguage.current = paywallPreviewLanguage.value
    previousLocalizedSubs.current = []
  }

  const localizedSubs = rawSubs.map((sub, index) => {
    let localizedSub = previousLocalizedSubs.current[index]
    if (isLoading) {
      // Don't update the previousLocalizedSubs until we have the
      // localizedStrings
      return sub
    }

    if (
      !localizedSub ||
      sub.value !== previousRawSubs.current[index]?.value ||
      sub.freeTrialValue !== previousRawSubs.current[index]?.freeTrialValue ||
      // Also check if the properties are different from the previous
      // localizedSub. This is to handle the case where the user changes
      sub.revision !== previousRawSubs.current[index]?.revision
    ) {
      localizedSub = {
        ...sub,
        value: localizer.safelyLocalizeTemplate(sub.value),
        freeTrialValue:
          sub.freeTrialValue != null
            ? localizer.safelyLocalizeTemplate(sub.freeTrialValue)
            : undefined,
      }
      previousLocalizedSubs.current[index] = localizedSub
    }

    return localizedSub
  })

  previousRawSubs.current = rawSubs

  return localizedSubs
}

export default useLocalizedSubstitutions
